import { useMutation, useQuery } from "@apollo/client";
import {
	Alert,
	Badge,
	Button,
	Card,
	Container,
	Group,
	Loader,
	Modal,
	Stack,
	Text,
	Textarea,
	TextInput,
	Title,
	Tooltip,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import {
	IconAlertTriangle,
	IconPlayerPlay,
	IconTerminal2,
} from "@tabler/icons-react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import type {
	GetAdminScriptsQuery,
	RunAdminScriptMutation,
} from "../../graphql/generated/types.gen";
import { GET_ADMIN_SCRIPTS, RUN_ADMIN_SCRIPT } from "../../graphql/queries";
import { useAuthCompat } from "../../hooks/useAuthCompat";
import { useIsMobile } from "../../hooks/useIsMobile";

type AdminScript = GetAdminScriptsQuery["adminScripts"][number];
type AdminScriptResult = RunAdminScriptMutation["runAdminScript"];

export const Route = createFileRoute("/admin/scripts")({
	component: AdminScriptsPage,
});

function AdminScriptsPage() {
	const { user, isLoading: authLoading } = useAuthCompat();
	const isMobile = useIsMobile();
	const [results, setResults] = useState<Record<string, AdminScriptResult>>({});

	useEffect(() => {
		if (authLoading) return;
		if (!user) {
			window.location.href = "/welcome";
			return;
		}
		if (!user.isEmployee) {
			window.location.href = "/";
		}
	}, [user, authLoading]);

	const { data, loading, error } = useQuery(GET_ADMIN_SCRIPTS, {
		skip: !user?.isEmployee,
	});

	const [runScript, { loading: running }] = useMutation(RUN_ADMIN_SCRIPT, {
		onCompleted: (resp) => {
			const result = resp.runAdminScript;
			setResults((prev) => ({ ...prev, [result.scriptName]: result }));
			notifications.show({
				color: result.success ? "green" : "red",
				title: result.success ? "Script completed" : "Script failed",
				message: result.message,
			});
		},
		onError: (error) => {
			notifications.show({
				color: "red",
				title: "Script error",
				message: error.message,
			});
		},
	});

	const [runningScriptName, setRunningScriptName] = useState<string | null>(
		null,
	);
	const [paramModal, setParamModal] = useState<{
		script: AdminScript;
		values: Record<string, string>;
	} | null>(null);
	// Retain the last-known modal title so it doesn't flicker to undefined
	// during Mantine's close animation after paramModal flips to null.
	const lastModalTitleRef = useRef<string>("");
	if (paramModal) {
		lastModalTitleRef.current = `Run "${paramModal.script.displayName}"`;
	}

	if (authLoading || loading || !user?.isEmployee) {
		return (
			<div className="flex items-center justify-center min-h-screen">
				<Loader size="lg" />
			</div>
		);
	}

	const scripts: AdminScript[] = data?.adminScripts ?? [];

	const executeScript = async (
		script: AdminScript,
		values: Record<string, string>,
	) => {
		setRunningScriptName(script.name);
		try {
			// STRING_LIST values are passed through verbatim; the server parses
			// the comma-separated form per the param descriptor. Blank optional
			// params are dropped so the wire payload matches the server's
			// "absent === empty" normalization.
			const filtered = script.params
				.map((p) => ({ key: p.key, value: values[p.key] ?? "" }))
				.filter(({ value }) => value.trim() !== "");
			const input = filtered.length === 0 ? undefined : filtered;
			await runScript({
				variables: { name: script.name, input },
			});
		} finally {
			setRunningScriptName(null);
		}
	};

	const confirmAndRun = (
		script: AdminScript,
		values: Record<string, string>,
	) => {
		if (!script.dangerous) {
			void executeScript(script, values);
			return;
		}
		modals.openConfirmModal({
			title: `Run "${script.displayName}"?`,
			centered: true,
			children: (
				<Text size="sm">
					This script is marked <strong>dangerous</strong>. Make sure you
					understand the consequences before continuing.
				</Text>
			),
			labels: { confirm: "Run", cancel: "Cancel" },
			confirmProps: { color: "red" },
			onConfirm: () => {
				void executeScript(script, values);
			},
		});
	};

	const handleRun = (script: AdminScript) => {
		if (script.params.length === 0) {
			confirmAndRun(script, {});
			return;
		}
		const initial: Record<string, string> = {};
		for (const p of script.params) initial[p.key] = "";
		setParamModal({ script, values: initial });
	};

	const submitParamModal = () => {
		if (!paramModal) return;
		const { script, values } = paramModal;
		const missing = script.params.find(
			(p) => p.required && !values[p.key]?.trim(),
		);
		if (missing) {
			notifications.show({
				color: "red",
				title: "Missing input",
				message: `"${missing.label}" is required.`,
			});
			return;
		}
		setParamModal(null);
		confirmAndRun(script, values);
	};

	return (
		<div className="h-[calc(100vh-var(--header-height))] overflow-auto bg-[var(--mantine-color-body)]">
			<Container size="lg" py="xl" px={isMobile ? "md" : "xl"}>
				<Group gap="md" mb="xs" wrap="nowrap">
					<IconTerminal2
						size={isMobile ? 24 : 32}
						color="var(--mantine-color-blue-6)"
					/>
					<Title order={isMobile ? 2 : 1}>Scripts</Title>
				</Group>
				<div className="mb-6">
					<Link to="/admin" style={{ color: "var(--mantine-color-anchor)" }}>
						&larr; Back to Admin
					</Link>
				</div>

				{error ? (
					<Alert
						color="red"
						title="Failed to load scripts"
						icon={<IconAlertTriangle size={16} />}
					>
						{error.message}
					</Alert>
				) : scripts.length === 0 ? (
					<Card shadow="sm" padding="xl" radius="md" withBorder>
						<Text size="lg" c="dimmed" ta="center">
							No admin scripts registered.
						</Text>
					</Card>
				) : (
					<Stack gap="md">
						{scripts.map((script) => {
							const result = results[script.name];
							const busy = running && runningScriptName === script.name;
							return (
								<Card
									key={script.name}
									shadow="sm"
									padding="lg"
									radius="md"
									withBorder
								>
									<Stack gap="sm">
										<Group
											justify="space-between"
											align="flex-start"
											wrap="nowrap"
										>
											<Stack gap={4}>
												<Group gap="xs" align="center">
													<Text fw={600} size="lg">
														{script.displayName}
													</Text>
													{script.dangerous && (
														<Badge
															color="red"
															variant="light"
															leftSection={<IconAlertTriangle size={12} />}
														>
															Dangerous
														</Badge>
													)}
												</Group>
												<Text size="sm" c="dimmed">
													{script.description}
												</Text>
												<Text size="xs" c="dimmed" ff="monospace">
													{script.name}
												</Text>
											</Stack>
											<Tooltip
												label="Another script is running"
												disabled={!running || busy}
												withArrow
											>
												{/* span intercepts hover; Mantine sets
												   pointer-events: none on disabled buttons,
												   which would otherwise swallow the Tooltip
												   trigger. */}
												<span style={{ display: "inline-block" }}>
													<Button
														color="violet"
														size="sm"
														leftSection={<IconPlayerPlay size={14} />}
														loading={busy}
														disabled={running && !busy}
														onClick={() => handleRun(script)}
													>
														Run
													</Button>
												</span>
											</Tooltip>
										</Group>
										{result && (
											<Card
												padding="sm"
												radius="sm"
												withBorder
												style={{
													borderColor: result.success
														? "var(--mantine-color-green-6)"
														: "var(--mantine-color-red-6)",
												}}
											>
												<Stack gap={4}>
													<Text fw={500} size="sm">
														{result.success
															? "Last run succeeded"
															: "Last run failed"}
													</Text>
													<Text size="sm">{result.message}</Text>
													{result.details.length > 0 && (
														<Stack gap={2} mt={4}>
															{result.details.map((d) => (
																<Text
																	key={d.key}
																	size="xs"
																	c="dimmed"
																	ff="monospace"
																>
																	{d.key}: {d.value}
																</Text>
															))}
														</Stack>
													)}
												</Stack>
											</Card>
										)}
									</Stack>
								</Card>
							);
						})}
					</Stack>
				)}
			</Container>

			<Modal
				opened={paramModal !== null}
				onClose={() => setParamModal(null)}
				title={lastModalTitleRef.current || undefined}
				centered
			>
				{paramModal && (
					<form
						onSubmit={(e) => {
							e.preventDefault();
							submitParamModal();
						}}
					>
						<Stack gap="md">
							{paramModal.script.params.map((param) => {
								const value = paramModal.values[param.key] ?? "";
								const setValue = (next: string) =>
									setParamModal((prev) =>
										prev
											? {
													...prev,
													values: { ...prev.values, [param.key]: next },
												}
											: prev,
									);
								if (param.type === "STRING_LIST") {
									return (
										<Textarea
											key={param.key}
											label={param.label}
											withAsterisk={param.required}
											description={param.description ?? undefined}
											placeholder="comma-separated"
											value={value}
											onChange={(e) => setValue(e.currentTarget.value)}
											autosize
											minRows={2}
										/>
									);
								}
								return (
									<TextInput
										key={param.key}
										label={param.label}
										withAsterisk={param.required}
										description={param.description ?? undefined}
										value={value}
										onChange={(e) => setValue(e.currentTarget.value)}
									/>
								);
							})}
							<Group justify="flex-end" gap="xs">
								<Button
									variant="default"
									onClick={() => setParamModal(null)}
									type="button"
								>
									Cancel
								</Button>
								<Button color="violet" type="submit">
									Continue
								</Button>
							</Group>
						</Stack>
					</form>
				)}
			</Modal>
		</div>
	);
}
